import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeOff, Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { XeroLogo } from "@/components/XeroLogo";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const shell = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
};

export default function Login() {
  const navigate = useNavigate();
  const { login, mfaSetup, mfaEnable, mfaVerify, setNewPassword } = useAuth();

  // stage: "credentials" | "password_change" | "setup" | "verify"
  const [stage, setStage] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // MFA
  const [qr, setQr] = useState(null); // { qr_code, secret }
  const [code, setCode] = useState("");

  // Password change
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [showNew, setShowNew] = useState(false);

  const goToStage = async (status) => {
    if (status === "password_change_required") {
      setStage("password_change");
    } else if (status === "mfa_setup_required") {
      const setup = await mfaSetup();
      setQr(setup);
      setStage("setup");
    } else if (status === "mfa_required") {
      setStage("verify");
    }
  };

  const resetToCredentials = () => {
    setStage("credentials");
    setError("");
    setCode("");
    setQr(null);
    setPassword("");
    setNewPass("");
    setConfirmPass("");
  };

  const handleCredentials = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email.trim(), password);
      await goToStage(res.status);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPass !== confirmPass) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const res = await setNewPassword(newPass, confirmPass);
      setCode("");
      await goToStage(res.status);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await mfaEnable(code.trim());
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await mfaVerify(code.trim());
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputBase =
    "w-full h-11 rounded-lg border border-slate-200 px-4 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-colors focus:border-[#13B5EA] focus:ring-2 focus:ring-[#13B5EA]/20";
  const primaryBtn =
    "w-full h-11 rounded-lg bg-[#13B5EA] text-white text-sm font-semibold shadow-sm transition-all hover:bg-[#0f9fce] active:scale-[0.99] disabled:opacity-60 flex items-center justify-center gap-2";

  return (
    <div className="min-h-screen w-full flex flex-col bg-[#f4f8fb]" data-testid="login-screen">
      <div className="w-full h-1.5 bg-[#13B5EA]" />

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <motion.div {...shell} className="w-full max-w-md">
          <div className="flex flex-col items-center text-center mb-8">
            <XeroLogo size={104} className="drop-shadow-sm" />
            <h1 className="mt-6 text-4xl font-bold tracking-tight text-[#1e2a32]">
              Xero <span className="text-[#13B5EA]">Payroll</span>
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              {stage === "credentials" && "Sign in to continue to your workspace"}
              {stage === "password_change" && "Set a new password to continue"}
              {stage === "setup" && "Set up two-factor authentication"}
              {stage === "verify" && "Enter your authentication code"}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-[0_10px_40px_-12px_rgba(19,181,234,0.35)] border border-slate-100 p-8">
            <AnimatePresence mode="wait">
              {/* ---------- CREDENTIALS ---------- */}
              {stage === "credentials" && (
                <motion.form
                  key="credentials"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleCredentials}
                  className="space-y-5"
                  data-testid="login-form"
                >
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                      Username
                    </label>
                    <input
                      id="email"
                      type="text"
                      autoComplete="username"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Please insert your email address here"
                      required
                      data-testid="login-email-input"
                      className={inputBase}
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        required
                        data-testid="login-password-input"
                        className={`${inputBase} pr-11`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        data-testid="toggle-password-visibility"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#13B5EA] transition-colors"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div data-testid="login-error" className="rounded-lg bg-red-50 border border-red-100 px-4 py-2.5 text-sm text-red-600">
                      {error}
                    </div>
                  )}

                  <button type="submit" disabled={loading} data-testid="login-submit-button" className={primaryBtn}>
                    {loading && <Loader2 size={16} className="animate-spin" />}
                    {loading ? "Signing in..." : "Sign in"}
                  </button>

                  <div className="flex items-center justify-between text-sm pt-1">
                    <button
                      type="button"
                      onClick={() => navigate("/signup")}
                      data-testid="signup-button"
                      className="font-medium text-[#13B5EA] hover:underline"
                    >
                      Sign up
                    </button>
                    <button type="button" data-testid="reset-button" className="font-medium text-slate-500 hover:text-[#13B5EA] transition-colors">
                      Reset password
                    </button>
                  </div>
                </motion.form>
              )}

              {/* ---------- PASSWORD CHANGE ---------- */}
              {stage === "password_change" && (
                <motion.form
                  key="password_change"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleSetPassword}
                  className="space-y-5"
                  data-testid="password-change-form"
                >
                  <div className="flex items-center gap-2 rounded-lg bg-[#e8f7fc] border border-[#bfe9f6] px-4 py-3 text-sm text-[#0b6a86]">
                    <ShieldCheck size={18} className="shrink-0" />
                    <span>Your password was reset by an administrator. Please set a new password.</span>
                  </div>

                  <div>
                    <label htmlFor="new-password" className="block text-sm font-medium text-slate-700 mb-1.5">
                      New password
                    </label>
                    <div className="relative">
                      <input
                        id="new-password"
                        type={showNew ? "text" : "password"}
                        value={newPass}
                        onChange={(e) => setNewPass(e.target.value)}
                        placeholder="At least 8 characters"
                        required
                        data-testid="new-password-input"
                        className={`${inputBase} pr-11`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNew((s) => !s)}
                        aria-label={showNew ? "Hide password" : "Show password"}
                        data-testid="toggle-new-password-visibility"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#13B5EA] transition-colors"
                      >
                        {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="confirm-password" className="block text-sm font-medium text-slate-700 mb-1.5">
                      Confirm new password
                    </label>
                    <input
                      id="confirm-password"
                      type={showNew ? "text" : "password"}
                      value={confirmPass}
                      onChange={(e) => setConfirmPass(e.target.value)}
                      placeholder="Re-enter your new password"
                      required
                      data-testid="confirm-password-input"
                      className={inputBase}
                    />
                  </div>

                  {error && (
                    <div data-testid="password-change-error" className="rounded-lg bg-red-50 border border-red-100 px-4 py-2.5 text-sm text-red-600">
                      {error}
                    </div>
                  )}

                  <button type="submit" disabled={loading} data-testid="set-password-button" className={primaryBtn}>
                    {loading && <Loader2 size={16} className="animate-spin" />}
                    {loading ? "Saving..." : "Set new password"}
                  </button>

                  <button type="button" onClick={resetToCredentials} className="w-full flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-[#13B5EA] transition-colors">
                    <ArrowLeft size={14} /> Back to sign in
                  </button>
                </motion.form>
              )}

              {/* ---------- MFA SETUP ---------- */}
              {stage === "setup" && (
                <motion.form
                  key="setup"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleEnable}
                  className="space-y-5"
                  data-testid="mfa-setup-form"
                >
                  <div className="flex items-center gap-2 rounded-lg bg-[#e8f7fc] border border-[#bfe9f6] px-4 py-3 text-sm text-[#0b6a86]">
                    <ShieldCheck size={18} className="shrink-0" />
                    <span>Two-factor authentication is required. Scan the code to finish setup.</span>
                  </div>

                  <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                    <li>Open Google Authenticator, Authy or 1Password.</li>
                    <li>Scan the QR code below.</li>
                    <li>Enter the 6-digit code it generates.</li>
                  </ol>

                  {qr?.qr_code && (
                    <div className="flex flex-col items-center gap-3">
                      <div className="p-3 bg-white rounded-xl border border-slate-200">
                        <img
                          src={qr.qr_code}
                          alt="MFA QR code"
                          data-testid="mfa-qr-code"
                          className="w-44 h-44"
                        />
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-400 mb-1">Can't scan? Enter this key manually:</p>
                        <code data-testid="mfa-secret" className="text-xs font-mono bg-slate-50 border border-slate-200 rounded px-2 py-1 break-all text-slate-700">
                          {qr.secret}
                        </code>
                      </div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="setup-code" className="block text-sm font-medium text-slate-700 mb-1.5">
                      Authentication code
                    </label>
                    <input
                      id="setup-code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="123456"
                      required
                      data-testid="mfa-code-input"
                      className={`${inputBase} tracking-[0.4em] text-center text-lg`}
                    />
                  </div>

                  {error && (
                    <div data-testid="mfa-error" className="rounded-lg bg-red-50 border border-red-100 px-4 py-2.5 text-sm text-red-600">
                      {error}
                    </div>
                  )}

                  <button type="submit" disabled={loading || code.length !== 6} data-testid="mfa-enable-button" className={primaryBtn}>
                    {loading && <Loader2 size={16} className="animate-spin" />}
                    {loading ? "Verifying..." : "Verify & enable"}
                  </button>

                  <button type="button" onClick={resetToCredentials} className="w-full flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-[#13B5EA] transition-colors">
                    <ArrowLeft size={14} /> Back to sign in
                  </button>
                </motion.form>
              )}

              {/* ---------- MFA VERIFY ---------- */}
              {stage === "verify" && (
                <motion.form
                  key="verify"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleVerify}
                  className="space-y-5"
                  data-testid="mfa-verify-form"
                >
                  <div className="flex items-center gap-2 rounded-lg bg-[#e8f7fc] border border-[#bfe9f6] px-4 py-3 text-sm text-[#0b6a86]">
                    <ShieldCheck size={18} className="shrink-0" />
                    <span>Enter the 6-digit code from your authenticator app.</span>
                  </div>

                  <div>
                    <label htmlFor="verify-code" className="block text-sm font-medium text-slate-700 mb-1.5">
                      Authentication code
                    </label>
                    <input
                      id="verify-code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="123456"
                      required
                      autoFocus
                      data-testid="mfa-code-input"
                      className={`${inputBase} tracking-[0.4em] text-center text-lg`}
                    />
                  </div>

                  {error && (
                    <div data-testid="mfa-error" className="rounded-lg bg-red-50 border border-red-100 px-4 py-2.5 text-sm text-red-600">
                      {error}
                    </div>
                  )}

                  <button type="submit" disabled={loading || code.length !== 6} data-testid="mfa-verify-button" className={primaryBtn}>
                    {loading && <Loader2 size={16} className="animate-spin" />}
                    {loading ? "Verifying..." : "Verify"}
                  </button>

                  <button type="button" onClick={resetToCredentials} className="w-full flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-[#13B5EA] transition-colors">
                    <ArrowLeft size={14} /> Back to sign in
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>

          <p className="mt-8 text-center text-xs text-slate-400">
            © {new Date().getFullYear()} Xero Payroll. All rights reserved.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
